<template>
  <div>
    <b-card
      no-body
      class="mb-2 router-toolbar-card"
    >
      <b-card-body class="router-toolbar">
        <div class="toolbar-field">
          <label>Market</label>
          <b-form-select
            v-model="market"
            :options="marketOptions"
            class="router-select"
            @change="fetchData"
          />
        </div>

        <div class="toolbar-field">
          <label>Pencere</label>
          <b-form-select
            v-model="hours"
            :options="hourOptions"
            class="router-select"
            @change="fetchData"
          />
        </div>

        <div class="toolbar-field">
          <label>Family</label>
          <b-form-select
            v-model="family"
            :options="familyOptions"
            class="router-select"
            @change="fetchData"
          />
        </div>

        <div class="toolbar-actions">
          <b-button
            variant="outline-primary"
            :disabled="loading"
            @click="fetchData"
          >
            <b-spinner
              v-if="loading"
              small
            />
            <feather-icon
              v-else
              icon="RefreshCwIcon"
            />
            Yenile
          </b-button>
        </div>
      </b-card-body>
    </b-card>

    <b-card
      no-body
      class="mb-2 router-summary-card"
    >
      <b-card-body class="router-summary">
        <div class="summary-header">
          <div>
            <h4 class="mb-0">
              Entry Router
            </h4>
            <div class="font-small-3 text-muted">
              Son {{ summaryWindow }} saat · {{ summary.filters.market || market }} · {{ summaryOverview.signals || 0 }} sinyal
            </div>
          </div>
          <b-badge
            variant="light-info"
            class="summary-badge"
          >
            {{ routerModuleSignals }} router kararı
          </b-badge>
        </div>

        <div class="summary-grid">
          <div class="summary-metric">
            <span>Toplam sinyal</span>
            <strong>{{ formatNum(summaryOverview.signals || 0, 0) }}</strong>
            <small>{{ formatNum(summaryOverview.openedRate || 0, 1) }}% açıldı</small>
          </div>
          <div class="summary-metric">
            <span>Router scope</span>
            <strong>{{ formatNum(routerModuleSignals, 0) }}</strong>
            <small>{{ formatNum(routerCoverageRate, 1) }}% kapsam</small>
          </div>
          <div class="summary-metric">
            <span>Router blok</span>
            <strong>{{ formatNum(routerBlockedRate, 1) }}%</strong>
            <small>ENTRY_ROUTER içinde</small>
          </div>
          <div class="summary-metric">
            <span>İyi kaçan</span>
            <strong>{{ formatNum(routerGoodSkippedRate, 1) }}%</strong>
            <small>blok/skip sonrası</small>
          </div>
          <div class="summary-metric">
            <span>Kötü açılan</span>
            <strong>{{ formatNum(routerBadOpenedRate, 1) }}%</strong>
            <small>aynı bucket</small>
          </div>
          <div class="summary-metric">
            <span>TP önce SL</span>
            <strong>{{ formatNum(routerTpBeforeSlRate, 1) }}%</strong>
            <small>router kalite izi</small>
          </div>
        </div>

        <div
          v-if="activeGuidanceText"
          class="summary-guidance"
        >
          <b-badge :variant="Number(activeGuidance.weightFactor || 1) >= 1 ? 'light-success' : 'light-warning'">
            {{ Number(activeGuidance.weightFactor || 1) >= 1 ? 'Destekleyici' : 'Sıkılaştır' }}
          </b-badge>
          <div class="summary-guidance-text">
            <strong>{{ activeGuidanceBucket }}</strong>
            <span>{{ activeGuidanceText }}</span>
          </div>
          <div class="summary-guidance-stats">
            <span>W {{ formatNum(activeGuidance.weightFactor || 1, 2) }}</span>
            <span>ΔS {{ signedNum(activeGuidance.scoreDelta || 0, 2) }}</span>
            <span>ΔScore {{ signedNum(activeGuidance.minEntryScoreDelta || 0, 2) }}</span>
            <span>ΔRR {{ signedNum(activeGuidance.minRemainingRewardRiskDelta || 0, 2) }}</span>
          </div>
        </div>

        <div
          v-if="actionRows.length"
          class="entry-preset-control"
        >
          <div>
            <b-form-checkbox
              v-model="entryPresetState.autoApplyEnabled"
              switch
              @change="onEntryPresetAutoApplyChange"
            >
              Önerileri otomatik işle
            </b-form-checkbox>
            <div class="font-small-3 text-muted">
              Açıkken Entry Router öneri kartlarındaki tüm presetler draft'a otomatik işlenir.
            </div>
          </div>
          <div
            v-if="entryPresetDraftSummary.length"
            class="entry-preset-draft"
          >
            <b-badge
              v-for="line in entryPresetDraftSummary"
              :key="line"
              variant="light-info"
            >
              {{ line }}
            </b-badge>
          </div>
        </div>

        <div
          v-if="actionRows.length"
          class="summary-action-grid"
        >
          <div
            v-for="item in actionRows"
            :key="item.key"
            class="summary-action-card"
          >
            <div class="summary-action-head">
              <b-badge :variant="item.variant">
                {{ item.badge }}
              </b-badge>
              <strong>{{ item.title }}</strong>
            </div>
            <div class="font-small-2 text-muted">
              {{ item.message }}
            </div>
            <div
              v-if="item.knobs && item.knobs.length"
              class="summary-action-knobs"
            >
              <span
                v-for="(knob, knobIndex) in item.knobs"
                :key="`${item.key}-knob-${knobIndex}`"
                class="summary-action-knob"
              >
                {{ knobLabel(knob) }}
              </span>
            </div>
            <div
              v-if="item.presets && item.presets.length"
              class="summary-action-presets"
            >
              <div
                v-for="(preset, presetIndex) in item.presets"
                :key="`${item.key}-preset-${presetIndex}`"
                class="summary-action-preset"
              >
                <div class="summary-action-preset-head">
                  <div>
                    <b-badge :variant="entryPresetScopeVariant(preset.scope)">
                      {{ entryPresetScopeLabel(preset.scope) }}
                    </b-badge>
                    <b-badge
                      v-if="isEntryPresetApplied(item, preset)"
                      variant="light-success"
                      class="ml-50"
                    >
                      İşlendi
                    </b-badge>
                    <strong class="ml-50">{{ preset.label }}</strong>
                  </div>
                  <b-button
                    size="sm"
                    variant="outline-success"
                    :disabled="isEntryPresetApplied(item, preset)"
                    @click="applyEntryRouterPreset(item, preset)"
                  >
                    {{ isEntryPresetApplied(item, preset) ? 'İşlendi' : "Draft'e işle" }}
                  </b-button>
                </div>
                <span>{{ preset.summary }}</span>
              </div>
            </div>
            <div class="font-small-3 summary-action-note">
              {{ item.recommendation }}
            </div>
          </div>
        </div>

        <div class="summary-breakdown-grid">
          <div class="summary-breakdown-panel">
            <div class="summary-panel-title">
              Router Reasons
            </div>
            <div
              v-if="reasonRows.length"
              class="reason-list"
            >
              <div
                v-for="item in reasonRows"
                :key="item.reason"
                class="summary-line"
              >
                <div class="summary-line-head">
                  <span class="summary-bucket">{{ item.reason }}</span>
                  <strong>{{ formatNum(item.count, 0) }}</strong>
                </div>
                <div class="font-small-2 text-muted">
                  {{ formatNum(item.rate, 1) }}% recent router karar
                </div>
              </div>
            </div>
            <div
              v-else
              class="font-small-3 text-muted"
            >
              Recent router nedeni yok.
            </div>
          </div>

          <div class="summary-breakdown-panel">
            <div class="summary-panel-title">
              Router Recent
            </div>
            <div
              v-if="routerRecentRows.length"
              class="reason-list"
            >
              <div
                v-for="item in routerRecentRows"
                :key="item.id"
                class="summary-line"
              >
                <div class="summary-line-head">
                  <span class="summary-bucket">{{ item.symbol }} {{ item.side }}</span>
                  <strong>{{ formatNum(item.signalScore || 0, 2) }}</strong>
                </div>
                <div class="font-small-2 text-muted">
                  {{ routerDecisionLabel(item) }}
                </div>
                <div
                  v-if="routerDecisionDetail(item)"
                  class="font-small-3 text-muted"
                >
                  {{ routerDecisionDetail(item) }}
                </div>
                <div
                  v-if="routerOutcomeDetail(item)"
                  class="font-small-3 text-muted"
                >
                  {{ routerOutcomeDetail(item) }}
                </div>
                <div class="font-small-3 text-muted">
                  {{ formatDateTime(item.detectedAt) }}
                </div>
              </div>
            </div>
            <div
              v-else
              class="font-small-3 text-muted"
            >
              Recent router kararı yok.
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <b-row>
      <b-col
        cols="12"
        xl="7"
        class="mb-2"
      >
        <b-card no-body>
          <b-card-body class="table-title-row">
            <h4 class="mb-0">
              Family Guidance
            </h4>
          </b-card-body>
          <b-table
            :items="familyRows"
            :fields="familyFields"
            responsive
            small
            show-empty
            empty-text="Family outcome yok."
          >
            <template #cell(bucket)="data">
              <strong>{{ data.item.bucket }}</strong>
            </template>
            <template #cell(openedRate)="data">
              {{ formatNum(data.item.openedRate, 1) }}%
            </template>
            <template #cell(blockedRate)="data">
              {{ formatNum(data.item.blockedRate, 1) }}%
            </template>
            <template #cell(goodSkippedRate)="data">
              {{ formatNum(data.item.goodSkippedRate, 1) }}%
            </template>
            <template #cell(badOpenedRate)="data">
              {{ formatNum(data.item.badOpenedRate, 1) }}%
            </template>
            <template #cell(tpBeforeSlRate)="data">
              {{ formatNum(data.item.tpBeforeSlRate, 1) }}%
            </template>
            <template #cell(guidance)="data">
              <strong>{{ formatNum((data.item.guidance && data.item.guidance.weightFactor) || 1, 2) }}</strong>
              <div class="text-muted font-small-3">
                {{ normalizeNote((data.item.guidance && data.item.guidance.note) || null) }}
              </div>
              <div class="text-muted font-small-3">
                ΔScore {{ signedNum((data.item.guidance && data.item.guidance.minEntryScoreDelta) || 0, 2) }}
                · ΔRR {{ signedNum((data.item.guidance && data.item.guidance.minRemainingRewardRiskDelta) || 0, 2) }}
              </div>
            </template>
          </b-table>
        </b-card>
      </b-col>

      <b-col
        cols="12"
        xl="5"
        class="mb-2"
      >
        <b-card no-body>
          <b-card-body class="table-title-row">
            <h4 class="mb-0">
              Recent Decisions
            </h4>
          </b-card-body>
          <b-table
            :items="routerRecentRows"
            :fields="recentFields"
            responsive
            small
            show-empty
            empty-text="ENTRY_ROUTER kararı yok."
          >
            <template #cell(symbol)="data">
              <strong>{{ data.item.symbol }}</strong>
              <div class="text-muted font-small-3">
                {{ data.item.side }} · {{ data.item.family || '-' }}
              </div>
            </template>
            <template #cell(decisionReason)="data">
              <strong>{{ routerDecisionLabel(data.item) }}</strong>
              <div
                v-if="routerDecisionDetail(data.item)"
                class="text-muted font-small-3"
              >
                {{ routerDecisionDetail(data.item) }}
              </div>
              <div
                v-if="routerOutcomeDetail(data.item)"
                class="text-muted font-small-3"
              >
                {{ routerOutcomeDetail(data.item) }}
              </div>
            </template>
            <template #cell(signalScore)="data">
              {{ formatNum(data.item.signalScore, 2) }}
            </template>
            <template #cell(signalMfePct)="data">
              {{ formatNum(data.item.signalMfePct, 2) }}
            </template>
            <template #cell(signalMaePct)="data">
              {{ formatNum(data.item.signalMaePct, 2) }}
            </template>
            <template #cell(detectedAt)="data">
              {{ formatDateTime(data.item.detectedAt) }}
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCol,
  BFormCheckbox,
  BFormSelect,
  BRow,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import {
  entryRouterDecisionDetail as formatEntryRouterDecisionDetail,
  entryRouterDecisionLabel as formatEntryRouterDecisionLabel,
  getEntryRouterDecisionMeta,
  outcomeMetricsDetail,
} from '@/utils/signalOutcomePresentation'
import { applyEntryQualityPreset } from '@/utils/entryQualityPresets'

export default {
  name: 'EntryRouterIndex',
  components: {
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormCheckbox,
    BFormSelect,
    BRow,
    BSpinner,
    BTable,
  },
  data() {
    return {
      entryPresetDraftStorageKey: 'traderio.entryRouter.entryPresetDraft',
      marketOptions: [
        { value: 'FUTURES', text: 'Futures' },
        { value: 'SPOT', text: 'Spot' },
      ],
      hourOptions: [
        { value: 24, text: '24 saat' },
        { value: 72, text: '72 saat' },
        { value: 168, text: '7 gün' },
      ],
      market: 'FUTURES',
      hours: 72,
      family: '',
      familyFields: [
        { key: 'bucket', label: 'Family' },
        { key: 'signals', label: 'Sinyal' },
        { key: 'openedRate', label: 'Açıldı' },
        { key: 'blockedRate', label: 'Blok' },
        { key: 'goodSkippedRate', label: 'İyi Kaçan' },
        { key: 'badOpenedRate', label: 'Kötü Açılan' },
        { key: 'tpBeforeSlRate', label: 'TP>SL' },
        { key: 'guidance', label: 'Guidance' },
      ],
      recentFields: [
        { key: 'symbol', label: 'Sembol' },
        { key: 'decisionReason', label: 'Gate' },
        { key: 'signalScore', label: 'Skor' },
        { key: 'signalMfePct', label: 'MFE' },
        { key: 'signalMaePct', label: 'MAE' },
        { key: 'detectedAt', label: 'Tarih' },
      ],
      entryPresetState: {
        autoApplyEnabled: false,
        autoAppliedPresetKeys: [],
        lastAppliedLabel: '',
      },
      entryPresetDraft: {
        signalEntry: {
          confirmationTicks: 1,
          maxSignalAgeBars: 2,
          minFavorableMovePct: 0,
          maxFavorableMovePct: 0.30,
          maxAdverseMovePct: 0.10,
          routerEnabled: true,
          minEntryScore: 0.30,
          minRemainingRewardRisk: 0.45,
          lateSignalPenaltyStartPct: 45,
          familyWeightEnabled: true,
          familyLookback: 50,
          familyMinSamples: 5,
          familyMinWeight: 0.65,
          familyMaxWeight: 1.20,
        },
        indicatorParams: {},
        positionManagement: {
          globalExposure: {
            enabled: false,
            blockOppositeSide: false,
          },
        },
      },
    }
  },
  computed: {
    summary() { return this.$store.getters['entryRouter/summary'] || {} },
    filters() { return this.$store.getters['entryRouter/filters'] || {} },
    loading() { return this.$store.getters['entryRouter/loading'] },
    summaryOverview() { return this.summary.overview || {} },
    summaryWindow() { return Number(this.summary.filters?.hours || this.hours || 72) },
    familyOptions() {
      const rows = this.summary.byFamily || []
      return [{ value: '', text: 'Tüm family' }].concat(
        rows.map(item => ({ value: item.bucket, text: `${item.bucket} (${this.formatNum(item.signals || 0, 0)})` })),
      )
    },
    routerModuleRow() {
      return (this.summary.byDecisionModule || []).find(item => item.bucket === 'ENTRY_ROUTER') || {}
    },
    routerModuleSignals() { return Number(this.routerModuleRow.signals || 0) },
    routerCoverageRate() {
      const total = Number(this.summaryOverview.signals || 0)
      return total > 0 ? (this.routerModuleSignals / total) * 100 : 0
    },
    routerBlockedRate() { return Number(this.routerModuleRow.blockedRate || 0) },
    routerGoodSkippedRate() { return Number(this.routerModuleRow.goodSkippedRate || 0) },
    routerBadOpenedRate() { return Number(this.routerModuleRow.badOpenedRate || 0) },
    routerTpBeforeSlRate() { return Number(this.routerModuleRow.tpBeforeSlRate || 0) },
    familyRows() { return (this.summary.byFamily || []).slice(0, 12) },
    routerRecentRows() {
      return (this.summary.recent || [])
        .filter(item => item.decisionModule === 'ENTRY_ROUTER')
        .slice(0, 12)
    },
    reasonRows() {
      const total = this.routerRecentRows.length
      const bucket = this.routerRecentRows.reduce((acc, item) => {
        const reason = this.routerDecisionLabel(item)
        acc[reason] = (acc[reason] || 0) + 1
        return acc
      }, {})
      return Object.keys(bucket)
        .map(reason => ({
          reason,
          count: bucket[reason],
          rate: total > 0 ? (bucket[reason] / total) * 100 : 0,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6)
    },
    activeGuidanceRow() {
      if (this.family) {
        return this.familyRows.find(item => item.bucket === this.family) || null
      }
      return this.familyRows.find(item => item.guidance && item.guidance.note) || this.familyRows[0] || null
    },
    activeGuidance() {
      if (this.family && this.summary.guidance) return this.summary.guidance
      return (this.activeGuidanceRow && this.activeGuidanceRow.guidance) || {}
    },
    activeGuidanceBucket() {
      if (this.family) return `${this.family} guidance`
      return this.activeGuidanceRow ? `${this.activeGuidanceRow.bucket} guidance` : 'Family guidance'
    },
    activeGuidanceText() {
      const note = this.normalizeNote(this.activeGuidance.note)
      return note
        ? `${note} · ${this.formatNum(this.activeGuidance.samples || 0, 0)} sinyal`
        : ''
    },
    actionRows() {
      return (this.summary.prescriptions || []).slice(0, 3).map(item => ({
        key: item.id,
        severity: item.severity,
        badge: this.actionBadge(item.severity),
        variant: this.actionVariant(item.severity),
        title: item.title,
        message: item.message,
        knobs: ((item.meta && item.meta.knobs) || []).slice(0, 3),
        presets: ((item.meta && item.meta.presets) || []).slice(0, 2),
        recommendation: item.recommendation,
      }))
    },
    entryPresetDraftSummary() {
      const signalEntry = this.entryPresetDraft.signalEntry || {}
      const indicatorParams = this.entryPresetDraft.indicatorParams || {}
      const globalExposure = this.entryPresetDraft.positionManagement?.globalExposure || {}
      const rows = [
        `Min score ${this.formatNum(signalEntry.minEntryScore, 2)}`,
        `Min RR ${this.formatNum(signalEntry.minRemainingRewardRisk, 2)}`,
        `Onay ${this.formatNum(signalEntry.confirmationTicks, 0)}`,
        `Geç ceza ${this.formatNum(signalEntry.lateSignalPenaltyStartPct, 0)}%`,
      ]
      if (indicatorParams.momentumQualityEnabled === true) rows.push('Momentum kalite açık')
      if (globalExposure.enabled === true) rows.push('Exposure açık')
      if (this.entryPresetState.lastAppliedLabel) rows.unshift(`Son işlendi: ${this.entryPresetState.lastAppliedLabel}`)
      return rows
    },
  },
  watch: {
    filters: {
      immediate: true,
      handler(value) {
        if (value.market) this.market = value.market
        if (value.hours) this.hours = Number(value.hours)
        this.family = value.family || ''
      },
    },
  },
  created() {
    this.restoreEntryPresetDraft()
    this.fetchData()
  },
  methods: {
    fetchData() {
      return this.$store.dispatch('entryRouter/fetch', {
        market: this.market,
        hours: this.hours,
        family: this.family,
        limit: 40,
      }).then(result => {
        this.maybeAutoApplyEntryRouterPresets()
        return result
      })
    },
    formatNum(value, digits = 2) {
      const n = Number(value || 0)
      if (!Number.isFinite(n)) return '0'
      return n.toLocaleString('tr-TR', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    },
    signedNum(value, digits = 2) {
      const n = Number(value || 0)
      if (!Number.isFinite(n)) return '0'
      const formatted = this.formatNum(Math.abs(n), digits)
      if (n > 0) return `+${formatted}`
      if (n < 0) return `-${formatted}`
      return formatted
    },
    normalizeNote(note) {
      if (!note) return null
      return String(note).replace(/_/g, ' ')
    },
    actionVariant(severity) {
      if (severity === 'RISK') return 'light-danger'
      if (severity === 'WATCH') return 'light-warning'
      return 'light-success'
    },
    actionBadge(severity) {
      if (severity === 'RISK') return 'Risk'
      if (severity === 'WATCH') return 'Dikkat'
      return 'Destek'
    },
    entryPresetScopeVariant(scope) {
      if (scope === 'GLOBAL_EXPOSURE') return 'light-info'
      if (scope === 'INDICATOR_PARAMS') return 'light-warning'
      return 'light-primary'
    },
    entryPresetScopeLabel(scope) {
      if (scope === 'GLOBAL_EXPOSURE') return 'Exposure'
      if (scope === 'INDICATOR_PARAMS') return 'Engine'
      return 'Signal Entry'
    },
    onEntryPresetAutoApplyChange(enabled) {
      if (!enabled) {
        this.entryPresetState.autoAppliedPresetKeys = []
        this.persistEntryPresetDraft()
        return
      }
      this.maybeAutoApplyEntryRouterPresets()
      this.persistEntryPresetDraft()
    },
    maybeAutoApplyEntryRouterPresets() {
      if (!this.entryPresetState.autoApplyEnabled) return
      this.actionRows.forEach(item => {
        ;(item.presets || []).forEach(preset => {
          if (!preset?.id || this.isEntryPresetApplied(item, preset)) return
          this.applyEntryRouterPreset(item, preset, { silent: true })
        })
      })
    },
    applyEntryRouterPreset(item, preset, options = {}) {
      if (!preset) return
      applyEntryQualityPreset(this.entryPresetDraft, preset)
      this.entryPresetDraft.signalEntry = this.normalizeSignalEntry(this.entryPresetDraft.signalEntry)
      this.entryPresetDraft.indicatorParams = this.normalizeIndicatorParams(this.entryPresetDraft.indicatorParams)
      this.entryPresetDraft.positionManagement = this.normalizePositionManagement(this.entryPresetDraft.positionManagement)
      this.entryPresetState.lastAppliedLabel = preset.label || ''
      const presetKey = this.entryRouterPresetKey(item, preset)
      if (!this.entryPresetState.autoAppliedPresetKeys.includes(presetKey)) {
        this.entryPresetState.autoAppliedPresetKeys.push(presetKey)
      }
      this.persistEntryPresetDraft()
      if (!options.silent) {
        this.entryPresetState.lastAppliedLabel = preset.label || ''
      }
    },
    entryRouterPresetKey(item, preset) {
      return `entry-router:${item?.key || 'summary'}:${preset?.id || ''}`
    },
    isEntryPresetApplied(item, preset) {
      return this.entryPresetState.autoAppliedPresetKeys.includes(this.entryRouterPresetKey(item, preset))
    },
    persistEntryPresetDraft() {
      try {
        window.localStorage.setItem(this.entryPresetDraftStorageKey, JSON.stringify({
          autoApplyEnabled: this.entryPresetState.autoApplyEnabled === true,
          autoAppliedPresetKeys: this.entryPresetState.autoAppliedPresetKeys,
          lastAppliedLabel: this.entryPresetState.lastAppliedLabel,
          draft: this.entryPresetDraft,
          updatedAt: new Date().toISOString(),
        }))
      } catch (e) {
        // localStorage kapalıysa ekran yine çalışsın; Test Studio sadece draft'ı göremez.
      }
    },
    restoreEntryPresetDraft() {
      try {
        const raw = window.localStorage.getItem(this.entryPresetDraftStorageKey)
        if (!raw) return
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') return
        const draft = parsed.draft && typeof parsed.draft === 'object' ? parsed.draft : {}
        this.entryPresetState.autoApplyEnabled = parsed.autoApplyEnabled === true
        this.entryPresetState.autoAppliedPresetKeys = Array.isArray(parsed.autoAppliedPresetKeys) ? parsed.autoAppliedPresetKeys : []
        this.entryPresetState.lastAppliedLabel = String(parsed.lastAppliedLabel || '')
        this.entryPresetDraft = {
          signalEntry: this.normalizeSignalEntry(draft.signalEntry),
          indicatorParams: this.normalizeIndicatorParams(draft.indicatorParams),
          positionManagement: this.normalizePositionManagement(draft.positionManagement),
        }
      } catch (e) {
        // Bozuk draft kullanıcı ekranını kırmasın.
      }
    },
    normalizeSignalEntry(value) {
      const d = {
        confirmationTicks: 1,
        maxSignalAgeBars: 2,
        minFavorableMovePct: 0,
        maxFavorableMovePct: 0.30,
        maxAdverseMovePct: 0.10,
        routerEnabled: true,
        minEntryScore: 0.30,
        minRemainingRewardRisk: 0.45,
        lateSignalPenaltyStartPct: 45,
        familyWeightEnabled: true,
        familyLookback: 50,
        familyMinSamples: 5,
        familyMinWeight: 0.65,
        familyMaxWeight: 1.20,
      }
      const v = value || {}
      const numberIn = (key, min, max) => {
        const raw = Number(v[key] ?? d[key])
        if (!Number.isFinite(raw)) return d[key]
        return Math.min(max, Math.max(min, raw))
      }
      return {
        confirmationTicks: Math.floor(numberIn('confirmationTicks', 0, 5)),
        maxSignalAgeBars: Math.floor(numberIn('maxSignalAgeBars', 0, 10)),
        minFavorableMovePct: numberIn('minFavorableMovePct', 0, 5),
        maxFavorableMovePct: numberIn('maxFavorableMovePct', 0, 10),
        maxAdverseMovePct: numberIn('maxAdverseMovePct', 0, 10),
        routerEnabled: v.routerEnabled !== false,
        minEntryScore: numberIn('minEntryScore', 0, 1),
        minRemainingRewardRisk: numberIn('minRemainingRewardRisk', 0, 10),
        lateSignalPenaltyStartPct: numberIn('lateSignalPenaltyStartPct', 0, 100),
        familyWeightEnabled: v.familyWeightEnabled !== false,
        familyLookback: Math.floor(numberIn('familyLookback', 5, 500)),
        familyMinSamples: Math.floor(numberIn('familyMinSamples', 0, 100)),
        familyMinWeight: numberIn('familyMinWeight', 0.1, 2),
        familyMaxWeight: numberIn('familyMaxWeight', 0.1, 3),
      }
    },
    normalizeIndicatorParams(value) {
      const v = value && typeof value === 'object' ? value : {}
      const out = { ...v }
      const numberIn = (key, min, max) => {
        const raw = Number(v[key])
        if (!Number.isFinite(raw)) return undefined
        return Math.min(max, Math.max(min, raw))
      }
      if (v.momentumQualityEnabled !== undefined) {
        out.momentumQualityEnabled = v.momentumQualityEnabled === true
      }
      const minBody = numberIn('minMomentumBodyRatio', 0, 1)
      if (minBody !== undefined) out.minMomentumBodyRatio = minBody
      const minClose = numberIn('minMomentumCloseLocation', 0, 1)
      if (minClose !== undefined) out.minMomentumCloseLocation = minClose
      return out
    },
    normalizePositionManagement(value) {
      const v = value && typeof value === 'object' ? value : {}
      const globalExposure = v.globalExposure && typeof v.globalExposure === 'object' ? v.globalExposure : {}
      return {
        ...v,
        globalExposure: {
          ...globalExposure,
          enabled: globalExposure.enabled === true,
          blockOppositeSide: globalExposure.blockOppositeSide === true,
        },
      }
    },
    knobKeyLabel(key) {
      if (key === 'minEntryScore') return 'Min Score'
      if (key === 'minRemainingRewardRisk') return 'Min RR'
      if (key === 'latePenalty') return 'Late Penalty'
      if (key === 'familyWeight') return 'Family Weight'
      if (key === 'confirmationStrictness') return 'Confirm'
      if (key === 'sameSideLock') return 'Same Side'
      if (key === 'exposureStrictness') return 'Exposure'
      return String(key || 'Knob')
    },
    knobLabel(knob) {
      if (!knob) return ''
      const key = this.knobKeyLabel(knob.key)
      const direction = knob.direction === 'UP' ? 'up' : 'down'
      const delta = Number(knob.delta)
      const deltaText = Number.isFinite(delta) && delta > 0 ? ` ${this.formatNum(delta, 2)}` : ''
      return `${key} ${direction}${deltaText}`
    },
    routerDecisionMeta(item) {
      return getEntryRouterDecisionMeta(item)
    },
    routerDecisionLabel(item) {
      return formatEntryRouterDecisionLabel(item)
    },
    routerDecisionDetail(item) {
      return formatEntryRouterDecisionDetail(item, this.formatNum)
    },
    routerOutcomeDetail(item) {
      return outcomeMetricsDetail(item, this.formatNum, {
        latePenalty: Number(this.routerDecisionMeta(item)?.latePenalty || 0),
      })
    },
    formatDateTime(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '—'
      return date.toLocaleString('tr-TR')
    },
  },
}
</script>

<style scoped>
.router-toolbar-card,
.router-summary-card {
  border: 1px solid #e7e7f2;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(34, 41, 47, 0.04);
  background: #fff;
}

.router-toolbar {
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 220px)) auto;
  gap: 1rem;
  align-items: end;
  padding: 1rem;
}

.toolbar-field label {
  display: block;
  margin-bottom: 0.35rem;
  color: #6e6b7b;
  font-size: 0.8rem;
  font-weight: 600;
}

.router-select {
  min-height: 38px;
  border: 1px solid #d8d6de;
  border-radius: 6px;
  background-color: #fff;
  color: #5e5873;
}

.toolbar-actions {
  display: flex;
  justify-content: flex-end;
  min-height: 38px;
}

.router-summary {
  padding: 1rem;
}

.summary-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.summary-badge {
  min-height: 32px;
  padding: 0.35rem 0.65rem;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.summary-metric {
  padding: 0.85rem 0.9rem;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  background: #fafafe;
}

.summary-metric span,
.summary-metric small {
  display: block;
  color: #6e6b7b;
}

.summary-metric span {
  font-size: 0.78rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
}

.summary-metric strong {
  display: block;
  font-size: 1.15rem;
  line-height: 1.2;
}

.summary-metric small {
  font-size: 0.76rem;
  margin-top: 0.25rem;
}

.summary-guidance {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.85rem 0.95rem;
  margin-bottom: 1rem;
  border: 1px solid #dfe7ff;
  border-radius: 6px;
  background: #f8fbff;
}

.summary-guidance-text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.summary-guidance-text span {
  color: #6e6b7b;
  font-size: 0.82rem;
}

.summary-guidance-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
  color: #5e5873;
  font-size: 0.76rem;
  font-weight: 600;
}

.summary-guidance-stats span {
  padding: 0.2rem 0.45rem;
  border-radius: 4px;
  background: #eef2ff;
}

.summary-action-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.entry-preset-control {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 0.95rem;
  margin-bottom: 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  background: #fbfbfe;
}

.entry-preset-draft {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.35rem;
  max-width: 58%;
}

.summary-action-card {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  padding: 0.85rem;
  background: #fcfcff;
}

.summary-action-head {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 0.45rem;
  min-width: 0;
}

.summary-action-head strong {
  min-width: 0;
  color: #5e5873;
  font-size: 0.84rem;
  line-height: 1.3;
}

.summary-action-note {
  margin-top: 0.45rem;
  color: #5e5873;
  line-height: 1.4;
}

.summary-action-presets {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.55rem;
}

.summary-action-preset {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.45rem 0.5rem;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  background: #fff;
}

.summary-action-preset-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.summary-action-preset strong {
  color: #5e5873;
  font-size: 0.75rem;
}

.summary-action-preset span {
  color: #6e6b7b;
  font-size: 0.72rem;
  line-height: 1.35;
}

.summary-action-knobs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.55rem;
}

.summary-action-knob {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0.12rem 0.42rem;
  border-radius: 4px;
  background: #f1eff7;
  color: #5e5873;
  font-size: 0.72rem;
  font-weight: 700;
}

.summary-breakdown-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.summary-breakdown-panel {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  padding: 0.85rem;
}

.summary-panel-title {
  margin-bottom: 0.6rem;
  color: #5e5873;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
}

.summary-line {
  padding: 0.5rem 0;
  border-top: 1px solid #f1eff7;
}

.summary-line:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.summary-line-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.summary-bucket {
  min-width: 0;
  color: #5e5873;
  font-size: 0.8rem;
  font-weight: 700;
}

.table-title-row {
  padding: 1rem 1rem 0.25rem;
}

@media (max-width: 1200px) {
  .summary-grid,
  .summary-action-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 991px) {
  .router-toolbar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .summary-guidance,
  .entry-preset-control,
  .summary-action-grid,
  .summary-breakdown-grid {
    grid-template-columns: 1fr;
  }

  .entry-preset-control {
    flex-direction: column;
  }

  .entry-preset-draft {
    justify-content: flex-start;
    max-width: none;
  }
}

@media (max-width: 767px) {
  .router-toolbar,
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
